
import type React from "react"

import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/lib/auth/auth-context"
import { authApi } from "@/lib/api/auth"
import { useProfile, useUpdateProfile } from "@/lib/api/use-auth"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { toast } from "sonner"

export function AccountSettings() {
  const { user } = useAuth()
  const currentRole = useCurrentRole()
  const { data: profile, isLoading: profileLoading } = useProfile()
  const updateProfile = useUpdateProfile()
  const [savingPassword, setSavingPassword] = useState(false)
  const [formData, setFormData] = useState({ firstName: "", lastName: "" })
  const seededRef = useRef(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })

  useEffect(() => {
    if (!seededRef.current && profile) {
      setFormData({
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
      })
      seededRef.current = true
    }
  }, [profile])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateProfile.mutateAsync({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      })
      toast.success("Settings saved")
    } catch (err) {
      console.error("Failed to update profile:", err)
      toast.error("Failed to update profile")
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match")
      return
    }
    setSavingPassword(true)
    try {
      await authApi.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" })
      toast.success("Password updated")
    } catch (err) {
      console.error("Failed to change password:", err)
      toast.error("Failed to change password")
    } finally {
      setSavingPassword(false)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-zinc-100">
          <h3 className="text-base font-semibold text-zinc-900">Profile Information</h3>
          <p className="text-sm text-zinc-400 mt-0.5">Update your personal information and email address</p>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center gap-6">
              <div className="size-20 rounded-2xl bg-zinc-100 flex items-center justify-center">
                <span className="text-2xl font-semibold text-zinc-600">{user ? getInitials(user.name) : "U"}</span>
              </div>
              <div>
                <div className="font-medium text-lg text-zinc-900">{user?.name}</div>
                <div className="text-sm text-zinc-400">
                  {user?.admin ? "Admin" : currentRole ?? ""}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstName" className="text-sm font-medium text-zinc-700">First Name</label>
                  <input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                    className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="lastName" className="text-sm font-medium text-zinc-700">Last Name</label>
                  <input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Doe"
                    className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-zinc-700">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={profile?.email ?? user?.email ?? ""}
                  readOnly
                  disabled
                  aria-describedby="email-hint"
                  className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-100 px-4 text-sm text-zinc-500 outline-none cursor-not-allowed"
                />
                <p id="email-hint" className="text-xs text-zinc-400">Email can't be changed. Contact support if you need to update it.</p>
              </div>
            </div>

            <button type="submit" disabled={updateProfile.isPending || profileLoading} className="h-11 px-6 rounded-2xl bg-zinc-900 text-white text-sm font-medium hover:bg-black transition-colors duration-100 disabled:opacity-50">
              {updateProfile.isPending ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-zinc-100">
          <h3 className="text-base font-semibold text-zinc-900">Change Password</h3>
          <p className="text-sm text-zinc-400 mt-0.5">Update your password to keep your account secure</p>
        </div>
        <div className="p-6">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="current-password" className="text-sm font-medium text-zinc-700">Current Password</label>
              <input id="current-password" type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} required className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150" />
            </div>

            <div className="space-y-2">
              <label htmlFor="new-password" className="text-sm font-medium text-zinc-700">New Password</label>
              <input id="new-password" type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} required className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150" />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirm-password" className="text-sm font-medium text-zinc-700">Confirm New Password</label>
              <input id="confirm-password" type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} required className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150" />
            </div>

            <button type="submit" disabled={savingPassword} className="h-11 px-6 rounded-2xl bg-zinc-900 text-white text-sm font-medium hover:bg-black transition-colors duration-100 disabled:opacity-50">
              {savingPassword ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
